import imageCompression from 'browser-image-compression'
import { enter, leave } from 'el-transition'
import { Controller } from '@hotwired/stimulus'
import { post } from '@rails/request.js'

export default class extends Controller {
  static targets = [
    'documentIdInput',
    'errorMessage',
    'filename',
    'filenameInput',
    'fileInput',
    'form',
    'pendingOverlay',
    'retry',
    'successfulOverlay',
  ]

  static values = {
    compressionInitialQuality: { default: 0.75, type: Number },
    compressionMaxWidthOrHeight: { default: 1500, type: Number },
    errorMessage: String,
    genericError: String,
    invalidCharacterError: String,
    invalidFileTypeError: String,
    status: String,
  }

  connect() {
    this.updateFilename()
  }

  dismiss() {
    this.element.remove()
  }

  errorMessageValueChanged(value, previousValue) {
    if (value !== '') {
      this.errorMessageTarget.innerHTML = this.errorMessageValue
      enter(this.errorMessageTarget)
    } else if (previousValue !== undefined && value === '') {
      this.errorMessageTarget.innerHTML = ''
      leave(this.errorMessageTarget)
    }
  }

  retry(event) {
    event.preventDefault()
    const promise = this.submit(this.documentIdInputTarget.value)
    this.dispatch('retry', { detail: { submitPromise: promise } })
  }

  statusValueChanged() {
    switch (this.statusValue) {
      case 'error':
        leave(this.pendingOverlayTarget)
        enter(this.retryTarget)
        break
      case 'pending':
        enter(this.pendingOverlayTarget)
        leave(this.retryTarget)
        break
      case 'success':
        leave(this.pendingOverlayTarget)
        enter(this.successfulOverlayTarget)
        break
      default:
    }
  }

  async submit(documentId) {
    this.documentIdInputTarget.value = documentId
    this.statusValue = 'pending'
    this.errorMessageValue = ''

    let response

    try {
      await this.#compressOriginalFile()

      response = await post(
        this.formTarget.action,
        {
          body: new FormData(this.formTarget),
          responseKind: 'json',
        },
      )
    } catch (error) {
      this.errorMessageValue = this.genericErrorValue
      this.statusValue = 'error'

      return this.statusValue
    }

    if (response.ok) {
      this.statusValue = 'success'
    } else if (response.unprocessableEntity) {
      const data = await response.json
      this.errorMessageValue = data.error
      this.statusValue = 'error'
    } else {
      this.errorMessageValue = this.genericErrorValue
      this.statusValue = 'error'
    }

    return this.statusValue
  }

  updateFilename() {
    const { files } = this.fileInputTarget

    if (files.length) {
      const filename = this.fileInputTarget.files[0].name
      const fileType = this.fileInputTarget.files[0].type

      if (/['"%]/.test(filename)) {
        this.errorMessageValue = this.invalidCharacterErrorValue
      }

      if (!/image\/.*/.test(fileType)) {
        this.errorMessageValue = [
          this.errorMessageValue,
          this.invalidFileTypeErrorValue,
        ].join(' ')
      }

      this.filenameTarget.innerHTML = filename
      this.filenameInputTarget.value = filename
    }
  }

  async #compressOriginalFile() {
    const compressionOptions = {
      initialQuality: this.compressionInitialQualityValue,
      maxWidthOrHeight: this.compressionMaxWidthOrHeightValue,
      preserveExif: true,
    }

    if (!this.compressed) {
      const original = this.fileInputTarget.files[0]
      const compressedBlob = await imageCompression(
        original,
        compressionOptions,
      )
      const compressedFile = new File(
        [compressedBlob],
        Valence.Utils.File.sanitizeFilename(original.name),
        { type: original.type },
      )
      const dataTransfer = new DataTransfer()
      dataTransfer.items.add(compressedFile)
      this.fileInputTarget.files = dataTransfer.files
      this.compressed = true
    }
  }
}
